import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import CookieConsentBanner from "./components/CookieConsent";
import { isShopPath } from "./lib/shopRoute";
import { redirectRetiredRoute } from "./lib/retiredRoutes";
import { isChallengePath } from "./lib/challengeRoute";
import { parseLegalPath } from "./legal/docs";
import { isLandingPath } from "@/surfaces/landing/lib/landingRoute";
import { isCheckoutPath } from "@/surfaces/checkout/lib/checkoutRoute";
import { parseShedRoute } from './shed/routes';

const SHED_M2A_PREVIEW = import.meta.env.VITE_SHED_M2A_PREVIEW === '1';
const ShedEntry = SHED_M2A_PREVIEW ? lazy(() => import('./shed/ShedEntry')) : null;

// Every route loads on demand. The route tests stay synchronous; only the page
// modules are deferred.
//
// The Golden Hour challenge waitlist was restored on 23 October 2026.
// The old assessment, Brain Starter and earlier sales experiments stay archived.
const GoldenHourWaitlistPage = lazy(() => import('./challenge/GoldenHourWaitlistPage'));
const TopNav = lazy(() => import("./sections/TopNav"));
const ShedHome = lazy(() => import("./shed-home/ShedHome"));
const Footer = lazy(() => import("./sections/Footer"));
const MidnightHome = lazy(() => import("./midnight/MidnightHome"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const LegalNotFound = lazy(() => import("./pages/LegalNotFound"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const LandingSurface = lazy(() => import("@/surfaces/landing/LandingSurface"));
const CheckoutSurface = lazy(() => import("@/surfaces/checkout/CheckoutSurface"));

/** Holds the page's own background while a route's module loads, so nothing flashes. */
function RouteFallback() {
  return <div className="min-h-[100dvh] bg-fd-bg" aria-busy="true" />;
}

/** The homepage's own canvas while its module loads: warm paper, or its dark theme. */
function ShedFallback() {
  return (
    <div
      className="min-h-[100dvh] bg-[#f7f5f0] [@media(prefers-color-scheme:dark)]:bg-[#0e0d0b]"
      aria-busy="true"
    />
  );
}

/** Public homepage and existing shop, product, checkout and legal routes. */
export default function MarketingV2App() {
  // A retired address is swapped for the homepage before the first render.
  const snapshot = () => { const pathname = redirectRetiredRoute(); return { pathname, search: window.location.search }; };
  const [{ pathname, search }, setLocation] = useState(snapshot);

  useEffect(() => {
    const onPopState = () => {
      setLocation(snapshot());
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // DIRECTION REVISED 2026-06-11 (same-day): "Light estate + midnight stage
  // panels." The light race-bib system is the operating brand across the
  // estate (better avatar reading comfort, direct-response convention,
  // voice-form alignment, bright-yellow thumbnail consistency). Foundry
  // Midnight survives as the brand's STAGE register — the particle flywheel
  // inside an ink panel in the light hero, video frames, cinematic moments —
  // and as the full-midnight homepage kept at ?v=midnight for reference.
  const shedRoute = SHED_M2A_PREVIEW ? parseShedRoute(pathname, search) : null;
  const variant = new URLSearchParams(search).get("v");
  const isMidnight = !shedRoute && variant === "midnight";

  // The body class lets out-of-tree portals (cookie banner) follow the
  // midnight tokens only when the full-midnight variant is being viewed.
  useEffect(() => {
    document.body.classList.toggle("mn-body", isMidnight);
    return () => document.body.classList.remove("mn-body");
  }, [isMidnight]);

  if (isChallengePath(pathname, search)) return <>
    <Suspense fallback={<div className="min-h-[100dvh] bg-[#0c0b09]" aria-busy="true" />}>
      <GoldenHourWaitlistPage />
    </Suspense>
    <CookieConsentBanner />
  </>;

  if (ShedEntry && shedRoute) return <>
    <Suspense fallback={<div style={{ minHeight: '100dvh', background: '#FFFFFF' }} aria-busy="true" />}>
      <ShedEntry route={shedRoute} search={search} />
    </Suspense><CookieConsentBanner theme="shed" />
  </>;

  // The shop — `/shop`. Everything for sale, on one page, read by people who
  // have never signed in. Unlike `/p/<slug>` and `/checkout/<sku>` it KEEPS the
  // site chrome: somebody browsing what is on offer should be able to get back
  // to the rest of the site, which is the opposite of what a landing page
  // wants. Local dev from the default surface: `?view=shop`.
  if (isShopPath(pathname, search))
    return (
      <SiteFrame>
        <Suspense fallback={<RouteFallback />}>
          <TopNav />
          <ShopPage />
          <Footer />
        </Suspense>
        <CookieConsentBanner />
      </SiteFrame>
    );

  // Product landing pages — `/p/<landingSlug>` (Phase 9,
  // docs/plans/2026-08-25-sku-shelf-portal-plan.md). They live on the marketing
  // host because they are read by people who have never signed in. The surface owns its own chrome:
  // no site nav, because a landing page with a dozen ways out is a landing page
  // nobody finishes. Local dev from the default surface: `?view=landing&p=<slug>`.
  if (isLandingPath(pathname, search))
    return (
      <>
        <Suspense fallback={<RouteFallback />}><LandingSurface /></Suspense>
        <CookieConsentBanner />
      </>
    );

  // Our own checkout — `/checkout/<sku>` (Plan 4,
  // docs/superpowers/plans/2026-08-26-classroom-plan-4.md). It lives on the
  // marketing host beside `/p/<slug>` for the same reason: whoever is reading
  // it has never signed in. Its own chrome, no site nav — a page with a dozen
  // ways out is a page nobody finishes paying on. Local dev from the default
  // surface: `?view=checkout&sku=<sku>`.
  if (isCheckoutPath(pathname, search))
    return (
      <>
        <Suspense fallback={<RouteFallback />}><CheckoutSurface /></Suspense>
        <CookieConsentBanner />
      </>
    );

  const legalRoute = parseLegalPath(pathname);

  if (legalRoute === "unknown")
    return (
      <>
        <Suspense fallback={<RouteFallback />}><LegalNotFound /></Suspense>
        <CookieConsentBanner />
      </>
    );
  if (legalRoute)
    return (
      <>
        <Suspense fallback={<RouteFallback />}><LegalPage slug={legalRoute} /></Suspense>
        <CookieConsentBanner />
      </>
    );

  if (isMidnight)
    return (
      <>
        <Suspense fallback={<RouteFallback />}><MidnightHome /></Suspense>
        <CookieConsentBanner />
      </>
    );

  // The homepage: Tom Ravenhill, a busy dad trying to post every day (Tom-led brand, 7 October 2026).
  return (
    <>
      <Suspense fallback={<ShedFallback />}><ShedHome /></Suspense>
      <CookieConsentBanner />
    </>
  );
}

/**
 * The estate: warm paper, the engineering grid, Geist. Every page that keeps
 * the site chrome sits inside this, so the homepage and the shop cannot end up
 * on two slightly different backgrounds.
 */
function SiteFrame({ children }: { children: ReactNode }) {
  return (
    <div
      className="min-h-[100dvh] overflow-x-hidden bg-fd-bg text-fd-text font-geist antialiased"
      style={{
        backgroundImage:
          "linear-gradient(var(--fd-grid) 1px, transparent 1px)," +
          "linear-gradient(90deg, var(--fd-grid) 1px, transparent 1px)",
        backgroundSize: "64px 64px",
        backgroundPosition: "-1px -1px",
      }}
    >
      {children}
    </div>
  );
}
